import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from 'config';

interface VideoItem {
  _id: string;
  title: string;
  url: string;
  order: number;
}

interface VideoCategory {
  _id: string;
  name: string;
  videos: VideoItem[];
  order: number;
}

const VideoTutorialsPage = () => {
  const [categories, setCategories] = useState<VideoCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Enhanced skeleton data with more realistic structure
  const skeletonCategories = [
    {
      id: 1,
      category: "Загрузка категории...",
      videos: Array(3).fill(null)
    },
    {
      id: 2,
      category: "Загрузка категории...",
      videos: Array(2).fill(null)
    }
  ];

  useEffect(() => {
    const fetchVideoLessons = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/api/video-lessons`);
        
        // Sort categories by order
        const sortedCategories = response.data.sort((a: VideoCategory, b: VideoCategory) => a.order - b.order);
        
        // Sort videos within each category by order
        sortedCategories.forEach((category: VideoCategory) => {
          category.videos.sort((a: VideoItem, b: VideoItem) => a.order - b.order);
        });
        
        setCategories(sortedCategories);
      } catch (err) {
        console.error('Error fetching video lessons:', err);
        // Don't set categories to empty array on error to maintain skeleton layout
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideoLessons();
  }, []);

  // Function to extract video ID from various platforms and convert to embed URL
  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    
    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = '';
      
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1];
        const ampersandPosition = videoId.indexOf('&');
        if (ampersandPosition !== -1) {
          videoId = videoId.substring(0, ampersandPosition);
        }
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1];
      }
      
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      const vimeoId = url.split('vimeo.com/')[1];
      return `https://player.vimeo.com/video/${vimeoId}`;
    }
    
    // Handle Rutube URLs
    if (url.includes('rutube.ru')) {
      // Handle different Rutube URL formats
      if (url.includes('/video/')) {
        const rutubeId = url.split('/video/')[1].split('/')[0];
        return `https://rutube.ru/play/embed/${rutubeId}`;
      } else if (url.includes('/embed/')) {
        // Already an embed URL, return as is
        return url;
      }
    }
    
    // Return original URL if not recognized
    return url;
  };

  // Render skeleton loading state
  const renderSkeletonState = () => (
    <div className="space-y-12">
      {skeletonCategories.map((category) => (
        <div key={category.id}>
          <div className="h-8 bg-gray-200 rounded w-64 mb-6 animate-pulse"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {category.videos.map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="aspect-w-16 aspect-h-9 bg-gray-200 animate-pulse"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Render actual content
  const renderActualContent = () => (
    <div className="space-y-12">
      {categories.map((category) => (
        <div key={category._id}>
          <h2 className="text-2xl font-semibold mb-6">{category.name}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {category.videos.map((video) => (
              <div key={video._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="aspect-w-16 aspect-h-9">
                  <iframe
                    src={getEmbedUrl(video.url)}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold">{video.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Link 
          to="/support" 
          className="text-primary hover:text-primary-dark transition-colors inline-flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Вернуться к технической поддержке
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-6">Видео уроки</h1>
      
      {isLoading || categories.length === 0 ? renderSkeletonState() : renderActualContent()}
    </div>
  );
};

export default VideoTutorialsPage; 